import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { getUserService } from '@src/container';
import type { UserDTO } from '@src/application/users/dto/user.dto';

/**
 * Hook: useBanUser
 * Bans a user from accessing the system (Admin only)
 * 
 * @param options - React Query mutation options
 * @returns Mutation result for banning user
 * 
 * @example
 * ```typescript
 * const { mutate: banUser, isPending, error } = useBanUser({
 *     onSuccess: (bannedUser) => {
 *         console.log('User banned:', bannedUser);
 *     }
 * });
 * 
 * // Usage
 * banUser('user-id-123');
 * ```
 */
export function useBanUser(
    options?: Omit<
        UseMutationOptions<UserDTO, Error, string>,
        'mutationFn'
    >
) {
    const userService = getUserService();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (userId: string) => {
            return await userService.ban(userId);
        },
        onSuccess: async (bannedUser, userId) => {
            // Invalidate and refetch users list
            queryClient.invalidateQueries({ queryKey: ['users'] });

            // Update the specific user query
            queryClient.setQueryData(['users', userId], bannedUser);
        },
        ...options,
    });
}
